/* eslint-disable max-lines */

import { useTranslate } from '@im/react-localization';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import React, { useEffect, useState } from 'react';
import {
  DataGrid,
  LoadingOverlay,
  NoRowsOverlay,
  SortedAscendingIcon,
  SortedDescendingIcon,
} from 'src/components/DataGrid';
import { GridRowId, GridRowModel } from '@mui/x-data-grid';
import { Pagination } from '@im/ui-lib/Pagination';
import { useTheme } from '@mui/system';
import { StyledText } from 'src/components/StyledTypography';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import {
  BidSearchQuery,
  BidSearchRequest,
  generateBidSearchQuery,
  useBLCAVGTimes,
  useBLCBidSearch,
  useBLCDefaultBids,
  useBLCDefaultBidsCount,
} from '../hooks/useBLCHooks';
import BidListSearchFields from './BidListSearchFields';
import { useGetColumns } from './components/useGetColumns';
import { useAccount } from '@azure/msal-react';
import SnackBarNotification from '../../../components/SnackBar/SnackBarNotification';
import {
  useParserReprocessBids,
  useParserReprocessBidsAudit,
} from '../hooks/useParserReprocess';
import { useSearchParams } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';

export default function BidList() {
  const translate = useTranslate();
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const countryCode = searchParams.get('countryCode');
  const companyCode = searchParams.get('companyCode');
  const pageSize = Number(searchParams.get('pageSize') ?? 10);
  const pageNumber = Number(searchParams.get('pageNumber') ?? 1);
  const [selectedRows, setSelectedRows] = useState<GridRowId[]>([]);
  const [searchQuery, setSearchQuery] = useState<BidSearchQuery>({});

  const avgTimes = useBLCAVGTimes({
    FromDate: searchQuery.FromDate ? searchQuery.FromDate : '',
    ToDate: searchQuery.ToDate ? searchQuery.ToDate : '',
    Vendor: searchQuery.Vendor ? searchQuery.Vendor.toString() : '',
    Version: 'V2',
  });
  const reprocessBids = useParserReprocessBids({
    countryCode: countryCode ?? '',
    documentIds: selectedRows.map((val) => {
      const rowIds = String(val).split('~');
      return `${rowIds[1]}~${rowIds[2]}`;
    }),
  });

  const isFilterApplied = !!Object.keys(searchQuery).length;

  const defaultBids = useBLCDefaultBids(
    {
      CompanyCode: companyCode ?? '',
      PageNumber: pageNumber,
      PageSize: pageSize,
    },
    !isFilterApplied
  );
  const defaultBidsCount = useBLCDefaultBidsCount(!isFilterApplied);

  const bidSearch = useBLCBidSearch(
    generateBidSearchQuery(
      searchQuery,
      {
        PageNumber: pageNumber,
        PageSize: pageSize,
      },
      countryCode ?? 'US'
    ) as BidSearchRequest,
    isFilterApplied
  );

  const data = isFilterApplied
    ? bidSearch.data?.value.response.bidStatusList ?? []
    : defaultBids.data?.value.response.bidStatusList ?? [];

  const defaultBidsCount_ = defaultBidsCount.isSuccess
    ? {
        ...defaultBids.data?.value.pageDetails,
        totalRecords: defaultBidsCount.data.totalBidCount,
      }
    : defaultBids.data?.value.pageDetails;

  const pageDetails = isFilterApplied
    ? bidSearch.data?.value.pageDetails
    : defaultBidsCount_;

  useEffect(() => {
    if (!isFilterApplied) {
      defaultBidsCount.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilterApplied, pageNumber, pageSize]);

  const requestPagination = () => (
    <Pagination
      data-testid="gds-blc-list-table-pagination"
      onPageChange={(page) => {
        searchParams.set('pageNumber', page.toString());
        setSearchParams(searchParams, { replace: true });
      }}
      onRowsPerPageChange={(pageSize) => {
        searchParams.set('pageSize', pageSize.toString());
        setSearchParams(searchParams, { replace: true });
      }}
      page={pageNumber}
      rowsPerPage={pageSize}
      totalCount={pageDetails?.totalRecords ?? 0}
    />
  );

  const [reprocessMsg, setReprocessMsg] = useState<string | undefined>();
  const account = useAccount();
  const reprocessBidsAudit = useParserReprocessBidsAudit({
    BusinessEntity: '',
    CompanyCode: companyCode ?? '',
    CountryCode: countryCode ?? '',
    DealNumber: selectedRows
      .map((val) => {
        const rowIds = String(val).split('~');
        return rowIds[2];
      })
      .join(','),
    Username: account?.username?.toLowerCase() ?? '',
    VendorName: selectedRows
      .map((val) => {
        const row = data.find(
          (r) =>
            `${r.customerSetId}~${r.parserHeaderID}~${r.bidNumber}~${r.bidEventHeaderId}-${r.bcn}~${r.bidId}~${r.id}` ===
            val
        );
        return row?.vendorName ?? '';
      })
      .join(','),
    VendorProgramName: selectedRows
      .map((val) => {
        const row = data.find(
          (r) =>
            `${r.customerSetId}~${r.parserHeaderID}~${r.bidNumber}~${r.bidEventHeaderId}-${r.bcn}~${r.bidId}~${r.id}` ===
            val
        );
        return row?.vendorProgramName ?? '';
      })
      .join(','),
  });
  const handleReprocessBids = () => {
    setReprocessMsg(undefined);
    reprocessBidsAudit
      .mutateAsync()
      .catch((error) => setReprocessMsg(error.response?.FailedBids.toString()));

    reprocessBids
      .mutateAsync()
      .then((response) =>
        response.FailedBids
          ? setReprocessMsg(response.FailedBids.toString())
          : undefined
      )
      .catch((error) => setReprocessMsg(error.response?.FailedBids.toString()));
  };
  const columns = useGetColumns();

  return (
    <Box p={2} width="100%">
      {reprocessBids.isSuccess && !reprocessMsg && (
        <SnackBarNotification
          Variant={'Success'}
          message={translate('BidLifeCycle.ReprocessSuccess')}
        />
      )}
      {(reprocessBids.isError || reprocessMsg) && (
        <SnackBarNotification
          Variant={'Failed'}
          message={translate('BidLifeCycle.ReprocessError', { reprocessMsg })}
        />
      )}
      <Stack
        alignContent={'center'}
        direction={'row'}
        justifyContent={'space-between'}
        mb={2}
      >
        <StyledText
          sx={{
            alignContent: 'center',
            display: 'grid',
            fontSize: '26px',
            fontWeight: 600,
            lineHeight: '28px',
          }}
        >
          {translate('BidLifeCycle.Heading')}
        </StyledText>
        <Box
          border={1}
          borderColor={theme.palette.grey[500]}
          borderRadius={'4px'}
        >
          <Stack direction={'row'} gap={2} p={2}>
            <StyledText>{`${translate(
              'BidLifeCycle.TimesAVGTimes'
            )}:`}</StyledText>
            <StyledText>{`${translate('BidLifeCycle.TimesImport')}: ${
              avgTimes.data?.value.response
                ? avgTimes.data?.value.response.importTime
                : 0
            } ${translate('BidLifeCycle.TimesMinutes')}`}</StyledText>
            <StyledText>{`${translate('BidLifeCycle.TimesCOG')}: ${
              avgTimes.data?.value.response
                ? avgTimes.data?.value.response.acopTime
                : 0
            } ${translate('BidLifeCycle.TimesMinutes')}`}</StyledText>
            <StyledText>{`${translate('BidLifeCycle.TimesAutoQuote')}: ${
              avgTimes.data?.value.response
                ? avgTimes.data?.value.response.autoQuoteTime
                : 0
            } ${translate('BidLifeCycle.TimesMinutes')}`}</StyledText>
          </Stack>
        </Box>
      </Stack>
      <BidListSearchFields
        pageSize={pageSize}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />
      <Stack
        direction={'row'}
        gap={1}
        justifyContent={'flex-end'}
        mb={1}
        mt={1}
      >
        <LoadingButton
          disabled={!selectedRows.length}
          loading={reprocessBids.isLoading}
          loadingPosition="start"
          onClick={() => handleReprocessBids()}
          startIcon={<AutorenewIcon />}
          variant="newContained"
        >
          {translate('BidLifeCycle.ReprocessBids')}
        </LoadingButton>
      </Stack>
      <DataGrid
        autoHeight
        checkboxSelection
        columnBuffer={columns.length}
        columns={columns}
        components={{
          ColumnSortedAscendingIcon: SortedAscendingIcon,
          ColumnSortedDescendingIcon: SortedDescendingIcon,
          LoadingOverlay: LoadingOverlay,
          NoRowsOverlay: NoRowsOverlay,
          Pagination: requestPagination,
        }}
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        disableSelectionOnClick
        getRowHeight={(params) => 'auto'}
        getRowId={(row: GridRowModel) =>
          `${row.customerSetId}~${row.parserHeaderID}~${row.bidNumber}~${row.bidEventHeaderId}-${row.bcn}~${row.bidId}~${row.id}`
        }
        hideFooterPagination={data.length <= 0}
        hideFooterSelectedRowCount
        isRowSelectable={(params) => params.row.currentStatus !== 'Deactivated'}
        loading={bidSearch.isLoading || defaultBids.isLoading}
        onSelectionModelChange={(selectionModel) =>
          setSelectedRows(selectionModel)
        }
        rows={data}
        selectionModel={selectedRows}
        sx={{
          '.MuiCheckbox-root': {
            color: '#0077d4 !important',
          },
          '.MuiCheckbox-root.Mui-disabled': {
            color: '#C4C4C4 !important',
          },
          '.MuiDataGrid-cell': {
            outline: 'transparent !important',
            overflow: 'hidden',
            padding: '0 20px',
          },
          '.MuiDataGrid-columnHeader--moving': {
            backgroundColor: 'transparent',
          },
          '.MuiDataGrid-columnHeaderCheckbox': {
            padding: '0px',
          },
          '.MuiDataGrid-columnHeaderTitle': {
            whiteSpace: 'normal',
          },
          '.MuiDataGrid-footerContainer>div': {
            flexWrap: 'wrap',
          },
          '.MuiDataGrid-footerContainer>div>div': {
            flex: 1,
          },
          '.MuiDataGrid-row': {
            minHeight: '52px !important',
          },
          '.MuiSvgIcon-root': {
            color: 'inherit',
          },
        }}
      />
    </Box>
  );
}
